import { useState, useCallback } from "react";
import { useStores } from "hooks/useStores";
import { useGoogleLogin } from "@react-oauth/google";

declare global {
  interface Window {
    AppleID: {
      auth: {
        signIn: () => Promise<{
          authorization: {
            id_token: string;
          };
        }>;
      };
    };
  }
}

interface LoadingState {
  google: boolean;
  apple: boolean;
}

interface UseSocialAuthOptions {
  onSuccess?: () => void;
  onError?: (provider: string, error: string) => void;
}

export const useSocialAuth = (options: UseSocialAuthOptions = {}) => {
  const { authStore } = useStores();
  const [isLoading, setIsLoading] = useState<LoadingState>({
    google: false,
    apple: false,
  });
  const { onSuccess, onError } = options;

  const triggerSignIn = useCallback(
    async (provider: "google" | "apple", socialToken: string) => {
      try {
        const response = await fetch(`/api/auth/signin/${provider}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            socialToken,
          }),
        });

        const result = await response.json();

        if (result.success) {
          try {
            await authStore.fetchCurrentUser();
            onSuccess?.();
          } catch (error) {
            console.error(
              "Failed to refresh auth state after OAuth signin:",
              error
            );
            onSuccess?.();
          }
        } else {
          const errorMessage = result.error?.message || "Unknown error";
          console.error(`${provider} signin error:`, result.error);
          onError?.(provider, errorMessage);
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error(`${provider} signin error:`, error);
        onError?.(provider, errorMessage);
      } finally {
        setIsLoading((prev) => ({ ...prev, [provider]: false }));
      }
    },
    [onSuccess, onError, authStore]
  );

  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        // Exchange authorization code for access token via our API
        const tokenResponse = await fetch("/api/auth/google/exchange", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: response.code,
          }),
        });

        if (!tokenResponse.ok) {
          throw new Error("Failed to exchange authorization code for token");
        }

        const tokenData = await tokenResponse.json();

        await triggerSignIn("google", tokenData.id_token);
      } catch (error) {
        console.error("Google token exchange error:", error);
        setIsLoading((prev) => ({ ...prev, google: false }));
        onError?.(
          "google",
          error instanceof Error ? error.message : "Token exchange failed"
        );
      }
    },
    onError: (error) => {
      setIsLoading((prev) => ({ ...prev, google: false }));
      onError?.(
        "google",
        error.error_description || error.error || "Google authentication failed"
      );
    },
    onNonOAuthError: () => {
      // Handle user cancellation or popup closure
      setIsLoading((prev) => ({ ...prev, google: false }));
    },
    flow: "auth-code",
  });

  const handleGoogleLogin = useCallback(() => {
    setIsLoading((prev) => ({ ...prev, google: true }));
    googleLogin();
  }, [googleLogin]);

  const appleLogin = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, apple: true }));
      const response = await window.AppleID.auth.signIn();
      if (response.authorization.id_token) {
        await triggerSignIn("apple", response.authorization.id_token);
      } else {
        throw new Error("Apple ID token not found in response");
      }
    } catch (error) {
      console.error("Apple login error:", error);
      setIsLoading((prev) => ({ ...prev, apple: false }));

      // Check if error is due to user cancellation
      if (error instanceof Error && error.message.includes("cancelled")) {
        console.log("Apple OAuth flow was cancelled by user");
        // Don't call onError for user cancellation
        return;
      }

      onError?.(
        "apple",
        error instanceof Error ? error.message : "Apple authentication failed"
      );
    }
  }, [triggerSignIn, onError]);

  return {
    triggerSignIn,
    googleLogin: handleGoogleLogin,
    appleLogin,
    isLoading,
  };
};
